import React, { useState, useRef, useEffect } from "react";
import { X, Save, Trash2, AlertTriangle, Edit } from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import { deleteAllChats, deleteUserAccount, updateUserName, getUserInfo, changePassword } from "../../../api";
import toast from 'react-hot-toast';

const SettingsModal = ({ isOpen, onClose, isDarkTheme }) => {
  const { user, logout } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [isEditingName, setIsEditingName] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showDeleteChatsConfirm, setShowDeleteChatsConfirm] = useState(false);
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const modalRef = useRef(null);
  const nameInputRef = useRef(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const data = await getUserInfo();
        setName(data.user_name);
        setEmail(data.user_email);
      } catch (error) {
        console.error("Failed to fetch user info:", error);
        // Fallback to context if API fails
        setName(user?.name || "");
        setEmail(user?.email || "");
      }
    };

    if (isOpen) {
      fetchUserInfo();
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isEditingName]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const handleSaveProfile = async () => {
    try {
      // Validate email format
      if (email && !/\S+@\S+\.\S+/.test(email)) {
        toast.error("Please enter a valid email address");
        return;
      }

      // If password fields are filled, validate them
      if (newPassword || confirmPassword) {
        if (!currentPassword) {
          toast.error("Current password is required to set a new password");
          return;
        }
        if (newPassword !== confirmPassword) {
          toast.error("New passwords do not match");
          return;
        }
        if (newPassword.length < 8) {
          toast.error("Password must be at least 8 characters long");
          return;
        }
  
        // Change user password
        const passwordResponse = await changePassword(currentPassword, newPassword);
        if (passwordResponse.status !== "success") {
          toast.error("Incorrect Password!");
          return;
        }
      }

      // Update name if it's been edited
      if (user?.name !== name) {
        await updateUserName(name);
      }
  
      toast.success("Profile updated successfully!");
      setIsEditingName(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error("Failed to update profile");
      console.error("Error updating profile:", error);
    }
  };

  const handleEditName = () => {
    setIsEditingName(true);
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    // Only save if the name has changed
    if (user?.name !== name) {
      handleSaveProfile();
    }
  };

  const handleDeleteAllChats = async () => {
    try {
      const res = await deleteAllChats();
      toast.success("All chats deleted successfully");
      setShowDeleteChatsConfirm(false);
    } catch (error) {
      toast.error("Failed to delete chats");
      console.error("Error deleting chats:", error);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm account deletion");
      return;
    }

    try {
      const res = await deleteUserAccount();
      toast.success("Account deleted successfully");
      logout();
      window.location.href = "/auth";
    } catch (error) {
      toast.error("Failed to delete account");
      console.error("Error deleting account:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[400]">
      <div
        ref={modalRef}
        className={`rounded-lg shadow-xl w-full mx-4 overflow-hidden 
          sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-4xl ${isDarkTheme ? "bg-gray-800 text-gray-300" : "bg-white text-gray-800"
          }`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDarkTheme ? "border-gray-700" : "border-gray-200"
          } lg:px-8 lg:py-5`}>
          <h2 className="text-xl font-semibold lg:text-2xl">Settings</h2>
          <button
            onClick={onClose}
            className={`${isDarkTheme ? "text-gray-300 hover:text-gray-100" : "text-gray-500 hover:text-gray-700"
              } focus:outline-none`}
          >
            <X className="w-5 h-5 lg:w-6 lg:h-6" />
          </button>
        </div>

        {/* Content */}
        <div className={`modal px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto lg:px-8 lg:py-6 lg:max-h-[calc(100vh-220px)] 
          ${
            isDarkTheme ? 
              "scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent" : 
              "scrollbar-thin scrollbar-track-transparent"
          }`}>
          <div className="space-y-6 lg:space-y-8">
            {/* Profile Section */}
            <div>
              <h3 className={`text-sm font-medium mb-3 lg:text-base lg:mb-4 ${isDarkTheme ? "text-gray-300" : "text-gray-700"
                }`}>Profile Information</h3>
              <div className="space-y-4 lg:space-y-5">
                <div>
                  <label htmlFor="name" className={`block text-xs mb-1 lg:text-sm lg:mb-2 ${isDarkTheme ? "text-gray-400" : "text-gray-500"
                    }`}>
                    Name
                  </label>
                  <div className={`flex items-center p-2 border rounded-md focus-within:ring-2 focus-within:ring-amber-500 focus-within:border-transparent lg:p-3 ${isDarkTheme ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-300"
                    }`}>
                    {isEditingName ? (
                      <input
                        id="name"
                        ref={nameInputRef}
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={handleNameBlur}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleNameBlur();
                        }}
                        className={`w-full bg-transparent focus:outline-none lg:text-lg ${isDarkTheme ? "text-gray-300" : "text-gray-800"
                          }`}
                      />
                    ) : (
                      <span className={`lg:text-lg ${isDarkTheme ? "text-gray-300" : "text-gray-800"
                        }`}>{name}</span>
                    )}
                    <Edit
                      className={`w-4 h-4 ml-auto cursor-pointer lg:w-5 lg:h-5 ${isDarkTheme ? "text-gray-400 hover:text-amber-500" : "text-gray-500 hover:text-amber-500"
                        }`}
                      onClick={handleEditName}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className={`block text-xs mb-1 lg:text-sm lg:mb-2 ${isDarkTheme ? "text-gray-400" : "text-gray-500"
                    }`}>
                    Email
                  </label>
                  <div className={`p-2 border rounded-md lg:p-3 lg:text-lg ${isDarkTheme ? "bg-gray-700 border-gray-600 text-gray-300" : "bg-gray-50 border-gray-300 text-gray-800"
                    }`}>
                    {email}
                  </div>
                </div>
              </div>
            </div>

            {/* Password Section */}
            <div>
              <h3 className={`text-sm font-medium mb-3 lg:text-base lg:mb-4 ${isDarkTheme ? "text-gray-300" : "text-gray-700"
                }`}>Change Password</h3>
              <div className="space-y-4 lg:space-y-5">
                <div>
                  <label htmlFor="currentPassword" className={`block text-xs mb-1 lg:text-sm lg:mb-2 ${isDarkTheme ? "text-gray-400" : "text-gray-500"
                    }`}>
                    Current Password
                  </label>
                  <input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent focus:outline-none lg:p-3 lg:text-lg ${isDarkTheme ? "bg-gray-700 border-gray-600 text-gray-300" : "border-gray-300"
                      }`}
                  />
                </div>
                <div>
                  <label htmlFor="newPassword" className={`block text-xs mb-1 lg:text-sm lg:mb-2 ${isDarkTheme ? "text-gray-400" : "text-gray-500"
                    }`}>
                    New Password
                  </label>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent focus:outline-none lg:p-3 lg:text-lg ${isDarkTheme ? "bg-gray-700 border-gray-600 text-gray-300" : "border-gray-300"
                      }`}
                  />
                </div>
                <div>
                  <label htmlFor="confirmPassword" className={`block text-xs mb-1 lg:text-sm lg:mb-2 ${isDarkTheme ? "text-gray-400" : "text-gray-500"
                    }`}>
                    Confirm New Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent focus:outline-none lg:p-3 lg:text-lg ${isDarkTheme ? "bg-gray-700 border-gray-600 text-gray-300" : "border-gray-300"
                      }`}
                  />
                </div>
              </div>
            </div>

            {/* Save Profile Button */}
            <div>
              <button
                onClick={handleSaveProfile}
                className="w-full px-4 py-3 bg-amber-500 text-white rounded-md hover:bg-amber-600 focus:outline-none flex items-center justify-center transition-colors duration-200 lg:py-4 lg:text-lg"
              >
                <Save className="w-4 h-4 mr-2 lg:w-5 lg:h-5" />
                Save Profile Changes
              </button>
            </div>

            {/* Danger Zone */}
            <div className={`pt-6 border-t ${isDarkTheme ? "border-gray-700" : "border-gray-200"
              } lg:pt-8`}>
              <h3 className={`text-sm font-medium mb-4 flex items-center lg:text-base lg:mb-5 ${isDarkTheme ? "text-red-400" : "text-red-600"
                }`}>
                <AlertTriangle className="w-4 h-4 mr-1 lg:w-5 lg:h-5 lg:mr-2" />
                Danger Zone
              </h3>

              {/* Delete All Chats */}
              <div className={`border rounded-lg p-4 mb-4 shadow-sm lg:p-6 lg:mb-5 ${isDarkTheme ? "bg-gray-700 border-red-400" : "bg-red-50 border-red-200"
                }`}>
                <h4 className={`text-sm font-medium mb-2 lg:text-base lg:mb-3 ${isDarkTheme ? "text-red-300" : "text-red-700"
                  }`}>Delete All Chats</h4>
                <p className={`text-xs mb-3 lg:text-sm lg:mb-4 ${isDarkTheme ? "text-red-300" : "text-red-600"
                  }`}>
                  This will permanently delete all your chat history. This action cannot be undone.
                </p>
                {!showDeleteChatsConfirm ? (
                  <button
                    onClick={() => setShowDeleteChatsConfirm(true)}
                    className={`px-3 py-2 rounded-md text-xs flex items-center transition-colors duration-200 lg:px-4 lg:py-3 lg:text-sm ${isDarkTheme
                        ? "bg-gray-600 text-red-300 hover:bg-gray-500"
                        : "bg-white text-red-600 border border-red-300 hover:bg-red-100"
                      }`}
                  >
                    <Trash2 className="w-3 h-3 mr-2 lg:w-4 lg:h-4" />
                    Delete All Chats
                  </button>
                ) : (
                  <div className="space-y-2 lg:space-y-3">
                    <div className={`flex items-center p-2 rounded-md lg:p-3 ${isDarkTheme ? "bg-gray-600 border-red-400" : "bg-red-100 border-red-300"
                      }`}>
                      <AlertTriangle className={`w-4 h-4 mr-2 flex-shrink-0 ${isDarkTheme ? "text-red-300" : "text-red-600"
                        } lg:w-5 lg:h-5`} />
                      <p className={`text-xs lg:text-sm ${isDarkTheme ? "text-red-300" : "text-red-700"
                        }`}>
                        Are you sure? This will delete all of your chat history permanently.
                      </p>
                    </div>
                    <div className="flex space-x-2 lg:space-x-3">
                      <button
                        onClick={handleDeleteAllChats}
                        className={`px-3 py-2 rounded-md text-xs hover:bg-red-700 focus:outline-none transition-colors duration-200 lg:px-4 lg:py-3 lg:text-sm ${isDarkTheme ? "bg-red-500 text-white" : "bg-red-600 text-white"
                          }`}
                      >
                        Confirm Delete
                      </button>
                      <button
                        onClick={() => setShowDeleteChatsConfirm(false)}
                        className={`px-3 py-2 rounded-md text-xs hover:bg-gray-100 focus:outline-none transition-colors duration-200 lg:px-4 lg:py-3 lg:text-sm ${isDarkTheme
                            ? "bg-gray-600 text-gray-300 hover:bg-gray-500"
                            : "bg-white text-gray-600 border border-gray-300"
                          }`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Delete Account */}
              <div className={`border rounded-lg p-4 shadow-sm lg:p-6 ${isDarkTheme ? "bg-gray-700 border-red-400" : "bg-red-50 border-red-200"
                }`}>
                <h4 className={`text-sm font-medium mb-2 lg:text-base lg:mb-3 ${isDarkTheme ? "text-red-300" : "text-red-700"
                  }`}>Delete Account</h4>
                <p className={`text-xs mb-3 lg:text-sm lg:mb-4 ${isDarkTheme ? "text-red-300" : "text-red-600"
                  }`}>
                  This will permanently delete your account and all associated data. This action cannot be undone.
                </p>
                {!showDeleteAccountConfirm ? (
                  <button
                    onClick={() => setShowDeleteAccountConfirm(true)}
                    className={`px-3 py-2 rounded-md text-xs flex items-center transition-colors duration-200 lg:px-4 lg:py-3 lg:text-sm ${isDarkTheme
                        ? "bg-gray-600 text-red-300 hover:bg-gray-500"
                        : "bg-white text-red-600 border border-red-300 hover:bg-red-100"
                      }`}
                  >
                    <Trash2 className="w-3 h-3 mr-2 lg:w-4 lg:h-4" />
                    Delete Account
                  </button>
                ) : (
                  <div className="space-y-3 lg:space-y-4">
                    <div className={`flex items-center p-2 rounded-md lg:p-3 ${isDarkTheme ? "bg-gray-600 border-red-400" : "bg-red-100 border-red-300"
                      }`}>
                      <AlertTriangle className={`w-4 h-4 mr-2 flex-shrink-0 ${isDarkTheme ? "text-red-300" : "text-red-600"
                        } lg:w-5 lg:h-5`} />
                      <p className={`text-xs lg:text-sm ${isDarkTheme ? "text-red-300" : "text-red-700"
                        }`}>
                        Type "DELETE" to confirm account deletion. This will permanently delete your account and all data.
                      </p>
                    </div>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE"
                      className={`w-full p-2 border rounded-md text-xs focus:ring-2 focus:ring-red-500 focus:border-transparent focus:outline-none lg:p-3 lg:text-sm ${isDarkTheme
                          ? "bg-gray-700 border-red-400 text-gray-300"
                          : "border-red-300"
                        }`}
                    />
                    <div className="flex space-x-2 lg:space-x-3">
                      <button
                        onClick={handleDeleteAccount}
                        className={`px-3 py-2 rounded-md text-xs hover:bg-red-700 focus:outline-none transition-colors duration-200 lg:px-4 lg:py-3 lg:text-sm ${isDarkTheme ? "bg-red-500 text-white" : "bg-red-600 text-white"
                          }`}
                        disabled={deleteConfirmText !== "DELETE"}
                      >
                        Permanently Delete Account
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteAccountConfirm(false);
                          setDeleteConfirmText("");
                        }}
                        className={`px-3 py-2 rounded-md text-xs hover:bg-gray-100 focus:outline-none transition-colors duration-200 lg:px-4 lg:py-3 lg:text-sm ${isDarkTheme
                            ? "bg-gray-600 text-gray-300 hover:bg-gray-500"
                            : "bg-white text-gray-600 border border-gray-300"
                          }`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;